import { Skeleton } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useRef } from 'react';

const SKELETON_ROW_COUNT = 10;

interface TableSkeletonResult<T> {
  dataSource: T[];
  columns: ColumnsType<T>;
  loading: boolean;
  isInitialLoad: boolean;
}

export function useTableSkeleton<T = any>(
  columns: ColumnsType<T>,
  dataSource: T[] | undefined,
  isLoading: boolean,
  rowCount = SKELETON_ROW_COUNT
): TableSkeletonResult<T> {
  const hasLoadedOnce = useRef(false);

  if (dataSource && dataSource.length > 0) {
    hasLoadedOnce.current = true;
  }

  const isInitialLoad = isLoading && !hasLoadedOnce.current;

  const skeletonColumns = useMemo(() => {
    if (!isInitialLoad) return columns;

    return columns.map((col) => ({
      ...col,
      filters: undefined,
      filterDropdown: undefined,
      sorter: undefined,
      render: () => <Skeleton.Button active size="small" block style={{ height: 16 }} />,
    })) as ColumnsType<T>;
  }, [columns, isInitialLoad]);

  const skeletonData = useMemo(() => {
    if (!isInitialLoad) return dataSource ?? [];

    return Array.from({ length: rowCount }, (_, i) => ({
      id: `skeleton-row-${i}`,
      key: `skeleton-row-${i}`,
    })) as unknown as T[];
  }, [isInitialLoad, dataSource, rowCount]);

  return {
    dataSource: skeletonData,
    columns: skeletonColumns,
    loading: isLoading && !isInitialLoad,
    isInitialLoad,
  };
}
